/**
 * A value in the Updates table, annotated with what it will do.
 *
 * "Updates" previously showed a wall of values with no indication of which ones
 * actually change anything — the user could see the incoming data but not the
 * edit. A conflicting value now shows what it would replace directly beneath
 * it, so the decision is visible in the row instead of only inside the
 * conflict resolver.
 *
 * Lives in its own file (rather than alongside getPropertyChange in
 * RowTableUpdateCells.tsx) because react-refresh forbids a component
 * declaration — exported or not — sharing a file with non-component exports.
 */

import { Badge } from '@/lib/base/badges/badges';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import type { PropertyChange } from './RowTableUpdateCells';
import { REVIEW_CELL_MAX_LENGTH } from './RowTableUpdateCells';

interface UpdateValueCellProps {
  value: unknown;
  propertyName: string;
  propertyType: PropertySchemaMap[string] | undefined;
  change: PropertyChange | undefined;
  existingObject: CrmObject | undefined;
}

export function UpdateValueCell({
  value,
  propertyName,
  propertyType,
  change,
  existingObject,
}: UpdateValueCellProps) {
  if (value === undefined || value === null || value === '') {
    return <span className='text-fg-quaternary'>—</span>;
  }

  const label = (
    <PropertyValueLabel
      value={value}
      propertyType={propertyType}
      fontSize='sm'
      maxLength={REVIEW_CELL_MAX_LENGTH}
    />
  );

  if (change === 'conflict') {
    return (
      <span className='flex flex-col gap-0.5'>
        <span className='font-medium text-utility-warning-800'>{label}</span>
        <span className='text-xs text-fg-quaternary line-through'>
          <PropertyValueLabel
            value={getPropertyValue<unknown>(
              existingObject?.properties?.[propertyName]
            )}
            propertyType={propertyType}
            fontSize='xs'
            maxLength={REVIEW_CELL_MAX_LENGTH}
          />
        </span>
      </span>
    );
  }

  if (change === 'added') {
    return (
      <span className='flex items-center gap-1.5'>
        {label}
        <Badge color='success' size='sm'>
          New
        </Badge>
      </span>
    );
  }

  return label;
}
